import { OPPORTUNITY_TYPE, VTO_OPPORTUNITY_TYPES } from '../../utils/laborOpportunityConstants';

/** Element-wise (a - b). Returns null if either operand is missing or empty. */
export const subtractSeries = (a, b) => {
  if (!a?.length || !b?.length) return null;
  return a.map((v, i) => (v || 0) - (b[i] || 0));
};

/** Convert an HC-per-interval series to labor hours, given each interval's
 *  duration in hours. Returns null if either input is missing. */
export const toLaborHours = (hcSeries, intervalHours) => {
  if (!hcSeries?.length || !intervalHours?.length) return null;
  return hcSeries.map((hc, i) => (hc || 0) * (intervalHours[i] || 0));
};

/** True if the series contains at least one non-trivial value. Used to drop
 *  rows whose data is entirely zero / missing so the table stays informative. */
export const hasSignal = (values) =>
  Array.isArray(values) && values.length > 0 && values.some((v) => Math.abs(v) > 1e-9);

export const seriesMaxAbs = (values) => values.reduce((m, v) => Math.max(m, Math.abs(v || 0)), 0);

/** RGBA tint scaled to a row's max — drives the heatmap effect on VET/VTO cells. */
const TINT_OPACITY_MIN = 0.05;
const TINT_OPACITY_RANGE = 0.18;
export const tintFor = (value, max, [r, g, b]) => {
  if (max <= 0 || !value) return undefined;
  const intensity = Math.min(1, Math.abs(value) / max);
  const opacity = TINT_OPACITY_MIN + intensity * TINT_OPACITY_RANGE;
  return `rgba(${r}, ${g}, ${b}, ${opacity.toFixed(3)})`;
};

/** Format a duration in seconds as "Hh Mm" (skipping zero parts). */
export const formatDuration = (seconds) => {
  const totalMinutes = Math.max(0, Math.round(seconds / 60));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

/** Aggregate VET/VTO counts and total HC across all opportunities. DSVTO
 *  rolls up into VTO via the shared theme set. */
export const buildVetVtoSummary = (laborOpportunities) =>
  (laborOpportunities || []).reduce(
    (acc, o) => {
      const hc = o.headcount || 0;
      if (o.opportunityType === OPPORTUNITY_TYPE.VET) {
        acc.vetCount += 1;
        acc.vetHC += hc;
      } else if (VTO_OPPORTUNITY_TYPES.has(o.opportunityType)) {
        acc.vtoCount += 1;
        acc.vtoHC += hc;
      }
      return acc;
    },
    { vetCount: 0, vetHC: 0, vtoCount: 0, vtoHC: 0 }
  );
